import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n';
import { apiClient } from '../api/client';
import { analytics } from '../hooks/useAnalytics';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';
import { STALE_TIMES } from '../constants/staleTimes';

// ─── types ────────────────────────────────────────────────────────────────────

interface Level {
  id: string;
  name: string;
  code: string;
  sort_order: number;
  is_active: boolean;
  spot_count: number;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function nameToCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ─── AdminLevelsPage ──────────────────────────────────────────────────────────

export default function AdminLevelsPage() {
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const { t }    = useTranslation();

  // Delete confirm state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteError, setDeleteError]         = useState<string | null>(null);

  // Add form state
  const [showAdd, setShowAdd]       = useState(false);
  const [addName, setAddName]       = useState('');
  const [addCode, setAddCode]       = useState('');
  const [addError, setAddError]     = useState<string | null>(null);

  const { data: levels, isLoading } = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn:  () => apiClient.get<{ data: Level[] }>('/admin/levels').then((r) => r.data.data),
    staleTime: STALE_TIMES.STATIC,
  });

  const sorted = [...(levels ?? [])].sort((a, b) => a.sort_order - b.sort_order);
  const maxSortOrder = sorted.length ? sorted[sorted.length - 1].sort_order : 0;

  // ── get translated level name ──

  function getTranslatedLevelName(code: string): string {
    return t(`level_${code}`) || code;
  }

  // ── toggle active ──

  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      apiClient.patch(`/admin/levels/${id}`, { is_active }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'levels'] });
      // Resident-facing /buildings/levels (used by RegisterSpotPage) uses a
      // different cache key — invalidate it too so residents see the change.
      void qc.invalidateQueries({ queryKey: ['levels'] });
    },
  });

  // ── delete ──

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/levels/${id}`),
    onSuccess: () => {
      const deletedLevel = levels?.find((l) => l.id === deleteConfirmId);
      if (deletedLevel) {
        analytics.trackBuildingLevelDeleted(deletedLevel.code);
      }
      setDeleteConfirmId(null);
      setDeleteError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'levels'] });
      void qc.invalidateQueries({ queryKey: ['levels'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'LEVEL_HAS_SPOTS') setDeleteError(t('cannot_delete_with_spots'));
      else setDeleteError(t('something_wrong'));
    },
  });

  // ── add ──

  const addMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/admin/levels', {
        name:       addName.trim(),
        code:       addCode.trim(),
        sort_order: maxSortOrder + 1,
      }),
    onSuccess: () => {
      analytics.trackBuildingLevelCreated(addCode.trim());
      setShowAdd(false);
      setAddName('');
      setAddCode('');
      setAddError(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'levels'] });
      void qc.invalidateQueries({ queryKey: ['levels'] });
    },
    onError: (err: unknown) => {
      const code = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      if (code === 'DUPLICATE_LEVEL_CODE') setAddError(t('code_exists'));
      else setAddError(t('something_wrong'));
    },
  });

  const addValid = addName.trim() !== '' && addCode.trim() !== '';

  return (
    <div style={page}>
      {/* Top bar */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 50,
        height: '64px', padding: '0 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backgroundColor: 'rgba(248,249,250,0.85)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <button
          onClick={() => navigate('/admin')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#005bc0', padding: '8px', display: 'flex', alignItems: 'center' }}
          aria-label={t('back')}
        >
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '22px' }} />
        </button>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ width: '38px' }} />
      </header>

      <div style={body}>
        {isLoading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px 0' }}>{t('loading')}</div>
        ) : (
          <>
            {sorted.map((level) => (
              <div key={level.id} style={{ ...card, marginBottom: '8px' }}>
                {/* Name row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '15px', fontWeight: 600, color: '#111', flex: 1 }}>
                    {getTranslatedLevelName(level.code)}
                  </span>
                  <span style={{ fontSize: '12px', color: '#9ca3af', flexShrink: 0 }}>
                    {level.spot_count} {level.spot_count === 1 ? t('spot_singular') : t('spots_plural')}
                  </span>
                </div>

                {/* Controls row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {/* Code chip */}
                  <span style={codeChip}>{level.code}</span>

                  <div style={{ flex: 1 }} />

                  {/* Active toggle */}
                  <button
                    onClick={() => toggleMutation.mutate({ id: level.id, is_active: !level.is_active })}
                    disabled={toggleMutation.isPending}
                    style={{
                      ...togglePill,
                      backgroundColor: level.is_active ? '#dcfce7' : '#f3f4f6',
                      color:           level.is_active ? '#16a34a' : '#9ca3af',
                      borderColor:     level.is_active ? '#bbf7d0' : '#e5e7eb',
                    }}
                  >
                    {level.is_active ? t('active') : t('inactive')}
                  </button>

                  {/* Delete */}
                  {level.spot_count > 0 ? (
                    <span title={t('has_spots')} style={{ ...trashBtn, opacity: 0.3, cursor: 'not-allowed' }}>🗑</span>
                  ) : (
                    <button
                      onClick={() => { setDeleteConfirmId(level.id); setDeleteError(null); }}
                      style={trashBtn}
                    >
                      🗑
                    </button>
                  )}
                </div>

                {/* Delete confirm */}
                {deleteConfirmId === level.id && (
                  <div style={{ marginTop: '10px', padding: '12px 14px', borderRadius: '10px', backgroundColor: '#fef2f2' }}>
                    <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '8px', fontWeight: 500 }}>
                      {t('delete_this_level')}
                    </div>
                    {deleteError && (
                      <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '6px' }}>{deleteError}</div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => deleteMutation.mutate(level.id)}
                        disabled={deleteMutation.isPending}
                        style={{ ...smallBtn, backgroundColor: '#dc2626', color: '#fff' }}
                      >
                        {deleteMutation.isPending ? t('deleting') : t('delete')}
                      </button>
                      <button
                        onClick={() => { setDeleteConfirmId(null); setDeleteError(null); }}
                        style={{ ...smallBtn, backgroundColor: '#f3f4f6', color: '#374151' }}
                      >
                        {t('cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Add level */}
            {!showAdd ? (
              <button onClick={() => setShowAdd(true)} style={addBtn}>
                {t('add_level_button')}
              </button>
            ) : (
              <div style={{ ...card, backgroundColor: '#dde8ff' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: '#005bc0', marginBottom: '10px' }}>
                  {t('new_level')}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <label style={fieldLabel}>{t('name')}</label>
                  <input
                    type="text"
                    placeholder={t('level_name_placeholder')}
                    value={addName}
                    onChange={(e) => {
                      setAddName(e.target.value);
                      setAddCode(nameToCode(e.target.value));
                    }}
                    style={fieldInput}
                    autoFocus
                  />
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <label style={fieldLabel}>{t('code')}</label>
                  <input
                    type="text"
                    placeholder={t('code_placeholder')}
                    value={addCode}
                    onChange={(e) => setAddCode(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    style={fieldInput}
                  />
                </div>
                {addError && (
                  <div style={{ fontSize: '12px', color: '#dc2626', marginBottom: '8px' }}>{addError}</div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => { setAddError(null); addMutation.mutate(); }}
                    disabled={!addValid || addMutation.isPending}
                    style={{ ...smallBtn, backgroundColor: '#2563eb', color: '#fff', opacity: (!addValid || addMutation.isPending) ? 0.5 : 1 }}
                  >
                    {addMutation.isPending ? t('adding') : t('add')}
                  </button>
                  <button
                    onClick={() => { setShowAdd(false); setAddName(''); setAddCode(''); setAddError(null); }}
                    style={{ ...smallBtn, backgroundColor: '#f3f4f6', color: '#374151' }}
                  >
                    {t('cancel')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── styles ───────────────────────────────────────────────────────────────────

const page: React.CSSProperties = {
  display:         'flex',
  flexDirection:   'column',
  minHeight:       '100dvh',
  maxWidth:        '430px',
  margin:          '0 auto',
  backgroundColor: '#f8f9fa',
  fontFamily:      "'Inter', system-ui, -apple-system, sans-serif",
};

const body: React.CSSProperties = { flex: 1, padding: '16px', overflowY: 'auto' };

const card: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius:    '14px',
  boxShadow:       '0 12px 32px -4px rgba(43,52,55,0.06)',
  padding:         '14px 16px',
};

const codeChip: React.CSSProperties = {
  fontSize: '11px', color: '#6b7280', backgroundColor: '#f1f4f6',
  borderRadius: '6px', padding: '2px 8px', fontFamily: "'Inter', monospace",
};

const togglePill: React.CSSProperties = {
  height: '26px', padding: '0 10px', borderRadius: '99px',
  border: '1px solid', fontSize: '12px', fontWeight: 500, cursor: 'pointer',
};

const trashBtn: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer',
  padding: '2px 4px',
};

const addBtn: React.CSSProperties = {
  width: '100%', height: '44px', borderRadius: '14px',
  border: '2px dashed #c7d5e8', background: 'none',
  color: '#005bc0', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
  marginTop: '4px',
};

const smallBtn: React.CSSProperties = {
  height: '32px', padding: '0 14px', borderRadius: '6px',
  border: 'none', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
};

const fieldLabel: React.CSSProperties = {
  display: 'block', fontSize: '11px', color: '#6b7280',
  fontWeight: 500, marginBottom: '4px',
};

const fieldInput: React.CSSProperties = {
  width: '100%', height: '36px', borderRadius: '6px',
  border: '1px solid rgba(0,91,192,0.2)', padding: '0 8px',
  fontSize: '14px', backgroundColor: '#ffffff', boxSizing: 'border-box',
};
